
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SuperAdminLicense, LicenseKey } from '@/types/licenseKey';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

interface VerificationResultsProps {
  verificationResults: {
    [key: string]: {
      isValid: boolean;
      message?: string;
      timestamp: Date;
    };
  };
  superAdminLicenses: SuperAdminLicense[];
  licenseKeys: LicenseKey[];
}

const VerificationResults: React.FC<VerificationResultsProps> = ({
  verificationResults,
  superAdminLicenses,
  licenseKeys
}) => {
  if (Object.keys(verificationResults).length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold mb-4">Recent Verification Results</h2>
      <div className="space-y-4">
        {Object.entries(verificationResults).map(([superAdminId, result]) => {
          const license = superAdminLicenses.find(l => l.superAdminId === superAdminId);
          const key = license ? licenseKeys.find(k => k.id === license.licenseKeyId) : null;
          const admin = license ? key?.superAdminName || superAdminId : superAdminId;
          
          return (
            <Alert key={superAdminId} variant={result.isValid ? 'default' : 'destructive'}>
              {result.isValid ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <AlertTitle>
                {result.isValid ? 'License Verified' : 'License Verification Failed'}
              </AlertTitle>
              <AlertDescription>
                <div className="mt-2">
                  <p><strong>Super Admin:</strong> {admin}</p>
                  <p><strong>Status:</strong> {result.isValid ? 'Valid' : 'Invalid'}</p>
                  {result.message && <p><strong>Message:</strong> {result.message}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    Verified at {format(result.timestamp, 'MMM d, yyyy HH:mm:ss')}
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          );
        })}
      </div>
    </div>
  );
};

export default VerificationResults;
